/* Casby theme behaviour.
   Interaction constants are taken from the design system's own components:
   Card.jsx      tilt  --tx=(0.5-py)*7   --ty=(px-0.5)*9   glow --gx/--gy = px/py*100%
   Button.jsx    tilt  --rx=-py*2*18     --ry=px*2*15      (TILT_X 18, TILT_Y 15)
   Drawer.jsx    open/close by toggling .is-open; Escape closes.
   Cart state is Shopify's, refreshed through the Section Rendering API. */
(function () {
  'use strict';

  var CARD_TX = 7;
  var CARD_TY = 9;
  var BTN_TILT_X = 18;
  var BTN_TILT_Y = 15;

  /* ---------- Card: glow follow + optional tilt ---------- */

  function bindCard(el) {
    if (el.dataset.cardBound) return;
    el.dataset.cardBound = '1';
    var tilt = el.hasAttribute('data-tilt');

    el.addEventListener('mousemove', function (e) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var px = (e.clientX - r.left) / r.width;
      var py = (e.clientY - r.top) / r.height;
      el.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
      el.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
      if (tilt) {
        el.classList.add('is-tilting');
        el.style.setProperty('--tx', ((0.5 - py) * CARD_TX).toFixed(2) + 'deg');
        el.style.setProperty('--ty', ((px - 0.5) * CARD_TY).toFixed(2) + 'deg');
      }
    });

    el.addEventListener('mouseleave', function () {
      el.classList.remove('is-tilting');
      el.style.setProperty('--tx', '0deg');
      el.style.setProperty('--ty', '0deg');
    });
  }

  /* ---------- Button: tilt toward the cursor ---------- */

  function bindButton(el) {
    if (el.dataset.btnBound) return;
    el.dataset.btnBound = '1';

    el.addEventListener('mousemove', function (e) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      el.classList.add('is-tilting');
      el.style.setProperty('--rx', (-py * 2 * BTN_TILT_X).toFixed(2) + 'deg');
      el.style.setProperty('--ry', (px * 2 * BTN_TILT_Y).toFixed(2) + 'deg');
    });

    el.addEventListener('mouseleave', function () {
      el.classList.remove('is-tilting');
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    });
  }

  /* Tilt and glow follow a mouse. On touch screens the browser fires one
     synthetic mousemove per tap and no mouseleave, which left the card stuck
     mid-tilt; skip binding there entirely. Desktop always matches. */
  var FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

  function initInteractions(root) {
    if (!FINE_POINTER.matches) return;
    var scope = root || document;
    scope.querySelectorAll('.m-card--interactive').forEach(bindCard);
    scope.querySelectorAll('.m-btn').forEach(bindButton);
  }

  /* ---------- product type filters ---------- */

  function initFilters() {
    document.querySelectorAll('[data-section-id]').forEach(function (section) {
      var grid = section.querySelector('[data-grid]');
      if (!grid || grid.dataset.filtersBound) return;
      grid.dataset.filtersBound = '1';

      var chips = section.querySelectorAll('[data-filter]');
      var label = section.querySelector('[data-result-label]');
      var items = Array.prototype.slice.call(grid.querySelectorAll('[data-filters]'));

      /* data-filters holds every handle the product matches: its collection
         handles plus its product-type handle. A product can sit in more than
         one filter collection, so this is a membership test, not equality. */
      function matches(item, key) {
        if (key === 'all') return true;
        var tokens = (item.dataset.filters || '').split(/\s+/);
        return tokens.indexOf(key) !== -1;
      }

      function countFor(key) {
        if (key === 'all') return items.length;
        return items.filter(function (i) { return matches(i, key); }).length;
      }

      section.querySelectorAll('[data-count-all]').forEach(function (n) { n.textContent = countFor('all'); });
      section.querySelectorAll('[data-count]').forEach(function (n) { n.textContent = countFor(n.dataset.count); });

      function apply(key) {
        var shown = 0;
        items.forEach(function (item) {
          var match = matches(item, key);
          item.hidden = !match;
          if (match) shown += 1;
        });
        if (label) label.textContent = shown + (shown === 1 ? ' product' : ' products');
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          chips.forEach(function (c) {
            var on = c === chip;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', String(on));
          });
          apply(chip.dataset.filter);
        });
      });

      apply('all');
    });
  }

  /* ---------- product gallery ---------- */

  function initGallery(root) {
    (root || document).querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryBound) return;
      gallery.dataset.galleryBound = '1';

      var slides = gallery.querySelectorAll('[data-slide]');
      var thumbs = gallery.querySelectorAll('[data-thumb]');
      var counter = gallery.querySelector('[data-counter]');
      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      var total = slides.length;
      if (total < 2) return;
      var current = 0;

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function show(index) {
        current = (index + total) % total;
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === current); });
        thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === current); });
        if (counter) counter.textContent = pad(current + 1) + ' / ' + pad(total);
      }

      if (prev) prev.addEventListener('click', function () { show(current - 1); });
      if (next) next.addEventListener('click', function () { show(current + 1); });
      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () { show(i); });
      });

      /* Swipe on touch screens. Touch events never fire for a mouse, so this
         adds nothing on desktop. A horizontal move past SWIPE_MIN that beats
         the vertical move changes slide; anything else is a scroll. */
      var SWIPE_MIN = 40;
      var stage = gallery.querySelector('.pd__stage');
      var startX = null;
      var startY = 0;
      if (stage) {
        stage.addEventListener('touchstart', function (e) {
          if (e.touches.length !== 1) { startX = null; return; }
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          if (startX === null) return;
          var dx = e.changedTouches[0].clientX - startX;
          var dy = e.changedTouches[0].clientY - startY;
          startX = null;
          if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
          show(dx < 0 ? current + 1 : current - 1);
        }, { passive: true });
      }

      show(0);
    });
  }

  /* ---------- accordion ---------- */

  function initAccordions(root) {
    (root || document).querySelectorAll('[data-accordion]').forEach(function (acc) {
      if (acc.dataset.accBound) return;
      acc.dataset.accBound = '1';

      acc.querySelectorAll('[data-acc-head]').forEach(function (head) {
        head.addEventListener('click', function () {
          var item = head.closest('.m-acc__item');
          var wasOpen = item.classList.contains('is-open');
          /* Accordion.jsx defaults to multiple={false}: opening one closes the rest. */
          acc.querySelectorAll('.m-acc__item').forEach(function (other) {
            other.classList.remove('is-open');
            var h = other.querySelector('[data-acc-head]');
            if (h) h.setAttribute('aria-expanded', 'false');
          });
          if (!wasOpen) {
            item.classList.add('is-open');
            head.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ---------- scroll reveal ---------- */

  /* Mirrors Reveal.jsx: one IntersectionObserver at threshold 0.15 that adds
     .is-in once and then stops watching. The blur-up itself is pure CSS
     (.m-rvl-js [data-reveal]); --i drives the 80ms-per-step stagger, and
     .m-split-w words stagger at 70ms off their parent's .is-in. */
  var REVEAL_THRESHOLD = 0.15;

  /* Grid items stagger across their row rather than by absolute position, so
     the last card of a 12-product grid is not held back by 11 steps. Items
     sharing a top offset are one row. */
  function staggerRows(grid) {
    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-reveal]'));
    var rowTop = null;
    var column = 0;
    items.forEach(function (item) {
      var top = item.offsetTop;
      if (rowTop === null || Math.abs(top - rowTop) > 4) {
        rowTop = top;
        column = 0;
      }
      item.style.setProperty('--i', column);
      column += 1;
    });
  }

  function initReveal(root) {
    var scope = root || document;

    scope.querySelectorAll('[data-grid], .product-grid').forEach(staggerRows);

    var targets = scope.querySelectorAll('[data-reveal], [data-reveal-split]');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { threshold: REVEAL_THRESHOLD });

    targets.forEach(function (el) {
      if (el.dataset.revealBound) return;
      el.dataset.revealBound = '1';
      io.observe(el);
    });
  }

  /* ---------- cart drawer ---------- */

  var lastFocus = null;

  function drawer() { return document.querySelector('[data-cart-drawer]'); }
  function overlay() { return document.querySelector('[data-cart-overlay]'); }

  function openCart() {
    var d = drawer(), o = overlay();
    if (!d || !o) return;
    lastFocus = document.activeElement;
    o.classList.add('is-open');
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-scroll-locked');
    var close = d.querySelector('[data-cart-close]');
    if (close) close.focus();
  }

  function closeCart() {
    var d = drawer(), o = overlay();
    if (!d || !o) return;
    o.classList.remove('is-open');
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('is-scroll-locked');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function setCartCount(count) {
    var host = document.querySelector('[data-cart-open]');
    if (!host) return;
    var badge = host.querySelector('[data-cart-count]');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'm-count';
        badge.setAttribute('data-cart-count', '');
        host.appendChild(badge);
      }
      badge.textContent = count > 99 ? '99+' : count;
    } else if (badge) {
      badge.remove();
    }
  }

  /* NOTE: no Accept: application/json here. With that header Shopify treats
     /cart as the AJAX cart endpoint and returns the cart object, ignoring
     ?sections= entirely — the Section Rendering API then never fires. */
  function refreshDrawer() {
    return fetch(window.routes.cart_url + '?sections=cart-drawer')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        var d = drawer();
        if (!d) return;
        if (!html) {
          console.error('[casby] cart-drawer section missing from response; keys: ' + Object.keys(data).join(','));
          return;
        }
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer]');
        if (!fresh) return;
        d.innerHTML = fresh.innerHTML;
        initInteractions(d);
      })
      .catch(function (err) { console.error('[casby] cart drawer refresh failed', err); });
  }

  /* ---------- delegated events ---------- */

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-open]')) {
      event.preventDefault();
      openCart();
      return;
    }

    if (event.target.closest('[data-cart-close]') || event.target.closest('[data-cart-overlay]')) {
      event.preventDefault();
      closeCart();
      return;
    }

    var qtyBtn = event.target.closest('[data-qty-change]');
    if (qtyBtn) {
      event.preventDefault();
      qtyBtn.disabled = true;
      fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(qtyBtn.dataset.line), quantity: Number(qtyBtn.dataset.qty) })
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          setCartCount(cart.item_count);
          return refreshDrawer();
        })
        .catch(function () { window.location.href = window.routes.cart_url; });
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-cart-add], form[action*="/cart/add"]');
    if (!form) return;

    event.preventDefault();
    var button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function () {
        return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        setCartCount(cart.item_count);
        return refreshDrawer();
      })
      .then(function () { openCart(); })
      .catch(function () { form.submit(); })
      .then(function () { if (button) button.disabled = false; });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var d = drawer();
    if (d && d.classList.contains('is-open')) closeCart();
  });

  /* ---------- boot ---------- */

  function init() {
    initInteractions(document);
    initFilters();
    initGallery(document);
    initAccordions(document);
    initReveal(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initInteractions(event.target);
    initFilters();
    initGallery(event.target);
    initAccordions(event.target);
    initReveal(event.target);
  });
})();
